import React, { useState } from 'react'
import Modal from 'components/modal'
import DeleteModal from 'components/delete'
import Button from 'components/button'
import { Table, ITableData } from 'components/table'
import Select from 'components/select'
import { ISite, ISitesModalOrganisation, ISiteUserRow } from 'types'
import api, { isUserToSiteArray } from 'helpers/api'
import { OrganisationRoles, ORG_PLAN_TIERS } from 'app-constants'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import Input from 'components/input'
import Subscription from 'components/subscription'
import UserSelect, { SelectedCount, UserOption } from 'components/userSelect'
import { formatShortDate } from 'helpers/date'

interface OrganisationSitesModalProps {
  open: boolean
  onClose: () => void
  organisation: ISitesModalOrganisation
  onChanged?: (organisation: ISitesModalOrganisation) => void
  filterTeamId?: string
  lockTeam?: boolean
  /** People who can be made site admins, for sites within the team dashboard */
  adminCandidates?: UserOption[]
}

const siteUserRows = (site: ISite | null): ISiteUserRow[] => {
  if (!site?.users) return []
  if (isUserToSiteArray(site.users)) return site.users.map((u) => ({ email: u.user.email, role: u.role }))
  return site.users
}

const OrganisationSitesModal: React.FC<OrganisationSitesModalProps> = ({
  open,
  onClose,
  organisation,
  onChanged,
  filterTeamId,
  lockTeam = false,
  adminCandidates
}) => {
  const { displayErrorMessage } = useApplicationStore()
  const { user } = useAuth()
  const isScaleUp = user?.team?.orgPlanTier === ORG_PLAN_TIERS.SCALE_UP

  const [view, setView] = useState<'list' | 'detail'>('list')
  const [selectedSite, setSelectedSite] = useState<ISite | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  const [siteName, setSiteName] = useState<string>('')
  const [siteTeamId, setSiteTeamId] = useState<string | null>(null)
  const [adminEmails, setAdminEmails] = useState<string[]>([])

  const isCreating = view === 'detail' && !selectedSite?.publicId

  const changeSiteTeam = async (site: ISite, teamId: string) => {
    if (!site.publicId) return
    setSaving(true)
    const usersPayload = isScaleUp ? siteUserRows(site) : undefined
    let response
    try {
      response = await api.updateSite(organisation.publicId, site.publicId, site.name, usersPayload, teamId)
    } catch (e) {
      displayErrorMessage(e)
      setSaving(false)
      return
    }
    const updatedSite = response.data
    const newOrganisation = {
      ...organisation,
      sites: organisation.sites.map((s) => (s.publicId === site.publicId ? updatedSite : s))
    }
    if (onChanged) onChanged(newOrganisation)
    setSaving(false)
  }

  /** Org users who can be picked as site admins, filtered to team members when filterTeamId is set */
  const orgUsers: UserOption[] =
    adminCandidates ??
    (organisation.users || [])
      .filter((u) => !filterTeamId || u.user.organisationTeamId === filterTeamId)
      .map((u) => ({
        publicId: u.user.email,
        name: u.user.name || u.user.email,
        email: u.user.email,
        createdAt: u.user.createdAt
      }))

  const lockedTeamName = lockTeam ? organisation.teams.find((t) => t.publicId === filterTeamId)?.name : undefined

  /** Every site belongs to a team, so there's no "No team" option */
  const teamOptions = (organisation.teams || []).map((t) => ({ label: t.name, value: t.publicId }))

  const filteredSites = filterTeamId ? organisation.sites.filter((s) => s.teamId === filterTeamId) : organisation.sites

  /** Sites table */
  const tableData: Array<Record<string, ITableData>> = filteredSites.map((s) => ({
    name: { value: s.name ?? '', label: s.name ?? '' },
    adminCount: {
      value: s.adminCount ?? 0,
      label: String(s.adminCount ?? 0)
    },
    hubCount: {
      value: s.hubCount ?? 0,
      label: String(s.hubCount ?? 0)
    },
    createdAt: {
      value: s.createdAt || '',
      label: s.createdAt ? formatShortDate(s.createdAt) : '—'
    },
    updatedAt: {
      value: s.updatedAt || '',
      label: s.updatedAt ? formatShortDate(s.updatedAt) : '—'
    },
    ...(!lockTeam
      ? {
          team: {
            value: s.teamName || '—',
            label: (
              <Select
                options={teamOptions}
                optionsSelected={s.teamId ? [s.teamId] : []}
                onOptionClick={(value) => {
                  if (value !== s.teamId) changeSiteTeam(s, value)
                }}
              />
            )
          }
        }
      : {}),
    manage: {
      value: s.publicId || '',
      label: (
        <Button
          internalType="outline"
          onClick={(e) => {
            e.stopPropagation()
            openSiteDetail(s)
          }}
        >
          Edit
        </Button>
      )
    }
  }))

  const openSiteDetail = (site: ISite) => {
    setSelectedSite(site)
    setSiteName(site.name)
    setSiteTeamId(site.teamId ?? null)
    setAdminEmails(siteUserRows(site).map((u) => u.email))
    setView('detail')
  }

  // Open in "create" mode
  const openCreateSite = () => {
    setSelectedSite(null)
    setSiteName('')
    setSiteTeamId(filterTeamId ?? null)
    setAdminEmails([])
    setView('detail')
  }

  const resetDetailState = () => {
    setView('list')
    setSelectedSite(null)
    setAdminEmails([])
    setSiteName('')
    setSiteTeamId(null)
  }

  const closeModal = () => {
    resetDetailState()
    onClose()
  }

  const toggleAdmin = (email: string) => {
    setAdminEmails((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]))
  }

  /** Ticked users are saved as site admins; anyone already on the site keeps their existing role */
  const buildUsersPayload = (): ISiteUserRow[] => {
    const existingRoles = new Map(siteUserRows(selectedSite).map((u) => [u.email, u.role]))
    return adminEmails.map((email) => ({ email, role: existingRoles.get(email) ?? OrganisationRoles.ADMIN }))
  }

  const saveDetail = async () => {
    const name = siteName.trim()
    if (!name || !siteTeamId) return

    setSaving(true)
    const sitePublicId = selectedSite ? selectedSite.publicId : undefined
    const usersPayload = isScaleUp ? buildUsersPayload() : undefined
    try {
      if (isCreating) {
        const response = await api.createSite(organisation.publicId, name, usersPayload, siteTeamId)
        const updatedSite = response.data
        const updatedOrganisation = {
          ...organisation,
          sites: [...organisation.sites, updatedSite]
        }
        if (onChanged) onChanged(updatedOrganisation)
      } else if (sitePublicId) {
        const response = await api.updateSite(organisation.publicId, sitePublicId, name, usersPayload, siteTeamId)
        const updatedSite = response.data
        const updatedOrganisation = {
          ...organisation,
          sites: organisation.sites.map((s) => (s.publicId === sitePublicId ? updatedSite : s))
        }
        if (onChanged) onChanged(updatedOrganisation)
      }

      setSaving(false)
      resetDetailState()
    } catch (e) {
      displayErrorMessage(e)
      setSaving(false)
    }
  }

  const deleteSite = async () => {
    const sitePublicId = selectedSite?.publicId
    if (!sitePublicId) return
    await api.deleteSite(organisation.publicId, sitePublicId)
    if (onChanged) {
      onChanged({ ...organisation, sites: organisation.sites.filter((s) => s.publicId !== sitePublicId) })
    }
    resetDetailState()
  }

  const getTitle = () => {
    if (view === 'list') return 'Organisation Sites'
    if (isCreating) return 'Create Site'
    return `Edit Site — ${selectedSite?.name ?? ''}`
  }

  return (
    <>
      <Modal
        id="organisation-sites-modal"
        open={open}
        setOpen={closeModal}
        title={getTitle()}
        backAction={view === 'detail' ? resetDetailState : undefined}
      >
        {view === 'list' && (
          <div className="flex flex-col gap-[12px]">
            {/* Header with New Site */}
            <div className="flex items-center mb-2">
              <div className="text-lg font-semibold mr-auto">Sites</div>
              <Button internalType="accept" onClick={openCreateSite}>
                + New Site
              </Button>
            </div>

            <div className="min-h-0 overflow-auto max-h-[60vh]">
              <Table
                data={tableData}
                include={[
                  { header: 'Name', id: 'name' },
                  { header: '# Admins', id: 'adminCount' },
                  { header: '# Hubs', id: 'hubCount' },
                  ...(!lockTeam ? [{ header: 'Team', id: 'team' }] : []),
                  { header: 'Created', id: 'createdAt' },
                  { header: 'Updated', id: 'updatedAt' },
                  { header: 'Action', id: 'manage' }
                ]}
                defaultSort="name"
                defaultSortAscending
              />
            </div>
          </div>
        )}

        {view === 'detail' && (
          <>
            {/* Site Name */}
            <div className="mb-4">
              <label htmlFor="site-name" className="block text-sm font-semibold mb-1">
                Site Name
              </label>
              <Input
                id="site-name"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                placeholder="Enter site name"
              />
            </div>

            {/* Team */}
            {!lockTeam && (
              <div className="mb-4">
                <label htmlFor="site-team" className="block text-sm font-semibold mb-1">
                  Team
                </label>
                <Select
                  options={teamOptions}
                  optionsSelected={siteTeamId ? [siteTeamId] : []}
                  onOptionClick={(value) => setSiteTeamId(value)}
                  placeholder="Choose a team"
                />
              </div>
            )}
            {lockTeam && (
              <div className="mb-4">
                <span className="block text-sm font-semibold mb-1">Team</span>
                <span className="text-sm text-muted">
                  This site will belong to <span className="font-semibold text-body">{lockedTeamName}</span>
                </span>
              </div>
            )}

            {/* Site Admins */}
            <div className="mb-5">
              <span className="block text-sm font-semibold mb-1">
                Site Admins <small className="font-normal text-muted">(optional - this can be done later)</small>
                {isScaleUp && <SelectedCount count={adminEmails.length} />}
              </span>
              {isScaleUp ? (
                <UserSelect users={orgUsers} selectedEmails={adminEmails} onToggle={toggleAdmin} showToolbar />
              ) : (
                <Subscription type="scale" message="assign site admins" />
              )}
              <span className="text-xs text-muted">
                Site admins get owner access to every hub in this site. They do not need Org Admin access.
                {isScaleUp && lockTeam && ` Only members of ${lockedTeamName} are listed.`}
              </span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {!isCreating && (
                <Button internalType="danger" onClick={() => setDeleteModalOpen(true)} disabled={saving}>
                  Delete This Site
                </Button>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Button internalType="outline" onClick={resetDetailState} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  internalType="accept"
                  onClick={saveDetail}
                  isLoading={saving}
                  disabled={!siteName.trim() || !siteTeamId}
                >
                  {isCreating ? 'Create Site' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal>

      {selectedSite && (
        <DeleteModal
          id="delete-site"
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          resourceName={selectedSite.name}
          deleteResource={deleteSite}
          onError={displayErrorMessage}
          notUndo
        />
      )}
    </>
  )
}

export default OrganisationSitesModal
